import {FC, useRef} from 'react'
import {Modal} from 'react-bootstrap'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'
import {useParams} from 'react-router-dom'
import {appsAPI} from '../../../../services/AppsService'
import {KTSVG} from '../../../../template/helpers'
import {Bitrix24AppCheck} from '../crm/bitrix24/Bitrix24AppCheck'
import {Bitrix24LocalAppInstruction} from '../crm/bitrix24/Bitrix24LocalAppInstruction'
import {bitrix24MarketAppUrl} from '../crm/bitrix24/bitrix24MarketAppUrl'
import {IBitrix24AppCheckRequest} from '../../../../types/apps/crm/biitrix24/Bitrix24AppCheckModel'

type Props = {
  variant: 'local' | 'cloud' | null
  credentials?: IBitrix24AppCheckRequest['credentials']
  onConfirm: () => void
  onClose: () => void
}

const messageIds = {
  local: {title: 'APPS.BITRIX24.MODE.LOCAL.TITLE', message: 'APPS.BITRIX24.MODE.LOCAL.MESSAGE'},
  cloud: {title: 'APPS.BITRIX24.MODE.CLOUD.TITLE', message: 'APPS.BITRIX24.MODE.CLOUD.MESSAGE'},
}

const modalsRoot = document.getElementById('root-modals') || document.body
const Bitrix24ModeSwitchModal: FC<Props> = ({variant, credentials, onConfirm, onClose}) => {
  const intl = useIntl()
  const lastVariant = useRef(variant)
  if (variant) lastVariant.current = variant
  const current = lastVariant.current
  const {appId} = useParams()
  const {data: app} = appsAPI.useFetchAppByIdQuery(appId!)
  const checkCredentials = current === 'local' ? credentials : undefined
  const openedAt = useRef(0)
  if (!variant) openedAt.current = 0
  else if (!openedAt.current) openedAt.current = Date.now()
  const {
    data: check,
    isFetching,
    fulfilledTimeStamp,
  } = appsAPI.useCheckBitrix24AppQuery(
    {appId: appId!, modeIsCloud: current === 'cloud', credentials: checkCredentials},
    {skip: variant === null}
  )
  const isInstalled = check?.state === 'OK' && (fulfilledTimeStamp ?? 0) >= openedAt.current

  return createPortal(
    <Modal
      show={variant !== null}
      onHide={onClose}
      centered
      size={current === 'local' ? 'lg' : undefined}
      dialogClassName={current === 'cloud' ? 'mw-700px' : undefined}
    >
      {current && (
        <>
          <Modal.Header closeButton>
            <Modal.Title>{intl.formatMessage({id: messageIds[current].title})}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {!isInstalled && (
              <div className='alert alert-danger d-flex align-items-center p-5 mb-3'>
                <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-danger svg-icon-2hx me-4' />
                <span className='fw-bold text-gray-800'>{intl.formatMessage({id: messageIds[current].message})}</span>
              </div>
            )}
            {!isInstalled && current === 'local' && (
              <Bitrix24LocalAppInstruction domain={app?.crm_domain} showCredentialsHint={false} />
            )}
            {!isInstalled && current === 'cloud' && (
              <div className='alert alert-primary d-flex flex-column mt-3 mb-3'>
                <h5 className='mb-0'>
                  {intl.formatMessage({id: 'APPS.MODAL.CREATE.BITRIX24.MESSAGE.TITTLE.START'})}{' '}
                  <a href={bitrix24MarketAppUrl(app?.crm_domain)} target='_blank' rel='noreferrer'>
                    {intl.formatMessage({id: 'APPS.MODAL.CREATE.BITRIX24.MESSAGE.LINK'})}
                  </a>{' '}
                  {intl.formatMessage({id: 'APPS.MODAL.CREATE.BITRIX24.MESSAGE.TITTLE.END'})}
                </h5>
              </div>
            )}
            <div className='alert alert-warning d-flex flex-column mt-3 mb-0'>
              <h5 className='mb-1'>{intl.formatMessage({id: 'APPS.BITRIX24.MODE.LINE.TITLE'})}</h5>
              <span className='text-gray-700'>{intl.formatMessage({id: 'APPS.BITRIX24.MODE.LINE.MESSAGE'})}</span>
            </div>
            <div className='mt-5'>
              <Bitrix24AppCheck modeIsCloud={current === 'cloud'} credentials={checkCredentials} hideHint>
                <div className='fw-bolder text-gray-800 me-2'>
                  {intl.formatMessage({id: 'APPS.BITRIX24.MODE.CONFIRM.STATUS'})}
                </div>
              </Bitrix24AppCheck>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button className='btn btn-sm btn-primary' onClick={onConfirm} disabled={!isInstalled || isFetching}>
              {intl.formatMessage({id: 'APPS.BITRIX24.MODE.CONFIRM.BUTTON'})}
            </button>
            <button className='btn btn-sm btn-light-primary' onClick={onClose}>
              {intl.formatMessage({id: 'BUTTON.CANCEL'})}
            </button>
          </Modal.Footer>
        </>
      )}
    </Modal>,
    modalsRoot
  )
}

export {Bitrix24ModeSwitchModal}
